import { BUSINESS_TIME_ZONE } from '@rental/domain'
import type { BookingRow } from '../board'

const ROME = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/** An instant as Rome's calendar date and the minute of that day on Rome's wall clock. */
export function romeClock(instant: string): { date: string; minute: number } {
  const parts = Object.fromEntries(
    ROME.formatToParts(new Date(instant)).map((part) => [part.type, part.value]),
  )
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minute: Number(parts.hour) * 60 + Number(parts.minute),
  }
}

/** "08:00" for a minute of the day. */
export function clockTime(minute: number): string {
  const hours = Math.floor(minute / 60)
  return `${String(hours).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
}

export type DayMark = { kind: 'pickup' | 'return' | 'ferry'; minute: number; code: string }

export type DayLineModel = {
  /** The first and last minute shown, on whole hours. */
  from: number
  to: number
  marks: readonly DayMark[]
  /** Now, when the board shows today. */
  now: number | null
  counts: { pickups: number; returns: number; ferries: number }
}

/** Never a sliver: at least this much of the day, widened on both sides. */
const LEAST_SPAN = 4 * 60
const DAY = 24 * 60

/**
 * The day at the port (UI-1c): the pick-ups, the returns and the customers'
 * ferries of the board's day, on Rome's clock. The span is the day's own,
 * from its first to its last moment on whole hours, never a fixed 08–20
 * (CLAUDE.md: never a hard-coded time); `null` when the day has nothing on it.
 */
export function dayLine(board: {
  date: string
  now: string
  pickups: readonly BookingRow[]
  returns: readonly BookingRow[]
}): DayLineModel | null {
  const onTheDay = (instant: string): number | null => {
    const clock = romeClock(instant)
    return clock.date === board.date ? clock.minute : null
  }
  const marks: DayMark[] = []
  for (const row of board.pickups) {
    const minute = onTheDay(row.startsAt)
    if (minute !== null) marks.push({ kind: 'pickup', minute, code: row.code })
    const ferry = row.ferry?.arrivalAt ? onTheDay(row.ferry.arrivalAt) : null
    if (ferry !== null) marks.push({ kind: 'ferry', minute: ferry, code: row.code })
  }
  for (const row of board.returns) {
    const minute = onTheDay(row.endsAt)
    if (minute !== null) marks.push({ kind: 'return', minute, code: row.code })
  }
  if (marks.length === 0) return null

  const now = onTheDay(board.now)
  const minutes = [...marks.map((mark) => mark.minute), ...(now === null ? [] : [now])]
  let from = Math.floor(Math.min(...minutes) / 60) * 60
  let to = Math.ceil(Math.max(...minutes) / 60) * 60
  while (to - from < LEAST_SPAN) {
    if (to < DAY) to += 60
    if (to - from < LEAST_SPAN && from > 0) from -= 60
  }
  const count = (kind: DayMark['kind']) => marks.filter((mark) => mark.kind === kind).length
  return {
    from,
    to,
    marks,
    now,
    counts: { pickups: count('pickup'), returns: count('return'), ferries: count('ferry') },
  }
}
